import { pick } from 'lodash';

import { Fields } from '../types';
import { LinkingProperty } from './linking';

export const sortProperties = <T extends LinkingProperty>(
  properties: T[],
  userOverrides?: Record<string, number>,
) => {
  const defaultOrder = properties.slice().sort((a: T, b: T) => {
    if ((a.pk ?? false) && !(b.pk ?? false)) {
      return -1;
    }
    if ((b.pk ?? false) && !(a.pk ?? false)) {
      return 1;
    }

    // Links should always come first. Multiple links should have a deterministic sort order based on the key.
    const aIsLink = a.buildLink !== undefined;
    const bIsLink = b.buildLink !== undefined;
    if (aIsLink && bIsLink) {
      return a.key > b.key ? 1 : -1;
    }
    if (aIsLink) {
      return -1;
    }
    if (bIsLink) {
      return 1;
    }
    return 0;
  });

  if (userOverrides === undefined) {
    return defaultOrder;
  }

  const sortedOverriddenIndexes = Object.entries(userOverrides).sort((a, b) => a[1] - b[1]);
  const sortedProperties = defaultOrder.filter(({ key }) => userOverrides[key] === undefined);
  for (const [key, index] of sortedOverriddenIndexes) {
    const field = defaultOrder.find((p) => p.key === key);
    if (field === undefined) {
      continue;
    }
    sortedProperties.splice(index, 0, field);
  }

  return sortedProperties;
};
export const sortGroupedFirst = <T extends LinkingProperty>(
  properties: T[],
  grouping: string[],
) => {
  return properties.slice().sort((a: T, b: T) => {
    const aIsGrouping = grouping.includes(a.key);
    const bIsGrouping = grouping.includes(b.key);

    if (aIsGrouping && !bIsGrouping) {
      return -1;
    }
    if (bIsGrouping && !aIsGrouping) {
      return 1;
    }

    if (aIsGrouping && bIsGrouping) {
      if (grouping.indexOf(a.key) < grouping.indexOf(b.key)) {
        return -1;
      }
      if (grouping.indexOf(a.key) > grouping.indexOf(b.key)) {
        return 1;
      }
    }
    return 0;
  });
};
export const sortFields = <T extends { fields: Fields }>(
  input: T,
  fields: Fields,
  grouping: string[],
  userOverrides?: Record<string, number>,
): T => ({
  ...input,
  fields: sortGroupedFirst(
    sortProperties(
      input.fields.map((field) => {
        const property = fields.find((f) => f.key === field.key);
        return {
          ...field,
          relation:
            field.relation ??
            (property?.relation !== undefined
              ? pick(property.relation, 'modelId', 'key', 'name')
              : undefined),
          model:
            field.model ??
            (property?.model !== undefined
              ? pick(property.model, 'modelId', 'name', 'propertyKey')
              : undefined),
        };
      }),
      userOverrides,
    ),
    grouping,
  ),
});

export const setFieldIndex = (
  key: string,
  fromIndex: number,
  toIndex: number,
  after: boolean,
  fieldOrder: Record<string, number>,
) => {
  if (fromIndex === toIndex) {
    return fieldOrder;
  }
  const newFieldOrder = { ...(fieldOrder ?? {}) };
  for (const [key, index] of Object.entries(newFieldOrder)) {
    if (toIndex < fromIndex && index > toIndex - (after ? 0 : 1) && index < fromIndex) {
      newFieldOrder[key] = index + 1;
    } else if (toIndex > fromIndex && index < toIndex + (after ? 1 : 0) && index > fromIndex) {
      newFieldOrder[key] = index - 1;
    }
  }
  newFieldOrder[key] = toIndex + (toIndex < fromIndex ? (after ? 1 : 0) : after ? 0 : -1);
  return newFieldOrder;
};
